import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import type { Rack } from '../../models/types';

interface RackFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deleteMode: boolean;
  preFillRow: number | null;
  preFillCol: number | null;
  pendingShelves: number;
  pendingSlotsPerShelf: number;
  pendingIsEndcap: boolean;
  localRacks: Rack[];
  deletedRackIds: Set<number>;
  onPreFillRowChange: (row: number | null) => void;
  onPreFillColChange: (col: number | null) => void;
  onShelvesChange: (shelves: number) => void;
  onSlotsPerShelfChange: (slots: number) => void;
  onIsEndcapChange: (checked: boolean) => void;
  onCreateAndClose: () => void;
  onDeleteAndClose: () => void;
}

export const RackFormModal = ({
  open,
  onOpenChange,
  deleteMode,
  preFillRow,
  preFillCol,
  pendingShelves,
  pendingSlotsPerShelf,
  pendingIsEndcap,
  localRacks,
  deletedRackIds,
  onPreFillRowChange,
  onPreFillColChange,
  onShelvesChange,
  onSlotsPerShelfChange,
  onIsEndcapChange,
  onCreateAndClose,
  onDeleteAndClose,
}: RackFormModalProps) => {
  const { t } = useTranslation('warehouseMapModule');

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg">
          <DialogPrimitive.Title className="text-sm font-semibold mb-2">
            {deleteMode
              ? t('rackForm.titleDelete', { count: deletedRackIds.size })
              : t('rackForm.titleNew', { count: localRacks.length })}
          </DialogPrimitive.Title>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs font-medium text-muted-foreground">
                {t('rackForm.rowLabel')}
                <input
                  id="rack-row"
                  type="number"
                  value={preFillRow ?? 0}
                  min={0}
                  onChange={(e) => onPreFillRowChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </label>

              <label className="block text-xs font-medium text-muted-foreground">
                {t('rackForm.colLabel')}
                <input
                  id="rack-col"
                  type="number"
                  value={preFillCol ?? 0}
                  min={0}
                  onChange={(e) => onPreFillColChange(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                  className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="block text-xs font-medium text-muted-foreground">
                {t('rackForm.shelvesLabel')}
                <input
                  id="rack-shelves"
                  type="number"
                  value={pendingShelves}
                  min={1}
                  onChange={(e) => onShelvesChange(e.target.value === '' ? 1 : parseInt(e.target.value, 10))}
                  className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </label>

              <label className="block text-xs font-medium text-muted-foreground">
                {t('rackForm.slotsLabel')}
                <input
                  id="rack-slots"
                  type="number"
                  value={pendingSlotsPerShelf}
                  min={1}
                  onChange={(e) => onSlotsPerShelfChange(e.target.value === '' ? 1 : parseInt(e.target.value, 10))}
                  className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </label>

              <div className="flex flex-col justify-end">
                <label className="text-xs font-medium text-muted-foreground">{t('rackForm.isEndcapLabel')}</label>
                <input
                  id="rack-is-endcap"
                  type="checkbox"
                  checked={pendingIsEndcap}
                  onChange={(e) => onIsEndcapChange(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors hover:bg-accent"
            >
              {t('rackForm.cancel')}
            </button>
            <button
              onClick={deleteMode ? onDeleteAndClose : onCreateAndClose}
              className={`inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                deleteMode
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : 'bg-primary text-primary-foreground hover:bg-primary/90'
              }`}
            >
              {deleteMode ? t('rackForm.delete') : t('rackForm.create')}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
